/**
 * Decode the existing comma-joined response using the configured full labels.
 * A comma inside a label is data, so splitting the response at every comma loses
 * information. Ambiguous or stale responses must be selected again.
 */
export const decodeCheckboxSelection = (
  response: string | null | undefined,
  options: readonly (string | null | undefined)[] | null | undefined
): string[] | null => {
  if (!response) return [];

  const labels = Array.from(
    new Set(options?.filter((option): option is string => !!option) ?? [])
  );
  const matches: string[][] = [];

  const search = (offset: number, selected: string[]) => {
    if (matches.length > 1) return;
    if (offset === response.length) {
      matches.push(selected);
      return;
    }

    for (const label of labels) {
      if (selected.includes(label) || !response.startsWith(label, offset))
        continue;

      const nextOffset = offset + label.length;
      if (nextOffset === response.length) {
        search(nextOffset, [...selected, label]);
      } else if (
        response[nextOffset] === "," &&
        nextOffset + 1 < response.length
      ) {
        search(nextOffset + 1, [...selected, label]);
      }
    }
  };

  search(0, []);
  return matches.length === 1 ? matches[0] : null;
};

export const encodeCheckboxSelection = (selected: readonly string[]): string =>
  selected.join(",");

/** Whether comma-joined selections can be decoded without guessing. */
export const areCheckboxOptionsUnambiguous = (
  options: readonly (string | null | undefined)[]
): boolean => {
  const labels = options.filter((option): option is string => !!option);
  if (new Set(labels).size !== labels.length) return false;

  // A trailing comma makes every encoded selection a sequence of complete
  // codewords. Compare two possible sequences while tracking the labels used
  // by each one; a checkbox cannot contribute the same codeword twice.
  const codewords = labels.map((label) => `${label},`);
  const emptySelection = "0".repeat(codewords.length);
  const withOption = (selection: string, index: number): string =>
    `${selection.slice(0, index)}1${selection.slice(index + 1)}`;
  type Comparison = {
    remainder: string;
    longerUsed: string;
    shorterUsed: string;
  };
  const comparisons: Comparison[] = [];
  const seen = new Set<string>();
  const addComparison = (comparison: Comparison) => {
    const key = `${comparison.longerUsed}:${comparison.shorterUsed}:${comparison.remainder}`;
    if (!seen.has(key)) {
      seen.add(key);
      comparisons.push(comparison);
    }
  };

  for (let left = 0; left < codewords.length; left++) {
    for (let right = 0; right < codewords.length; right++) {
      if (left !== right && codewords[left].startsWith(codewords[right])) {
        addComparison({
          remainder: codewords[left].slice(codewords[right].length),
          longerUsed: withOption(emptySelection, left),
          shorterUsed: withOption(emptySelection, right),
        });
      }
    }
  }

  for (
    let comparisonIndex = 0;
    comparisonIndex < comparisons.length;
    comparisonIndex++
  ) {
    const { remainder, longerUsed, shorterUsed } = comparisons[comparisonIndex];
    for (let optionIndex = 0; optionIndex < codewords.length; optionIndex++) {
      if (shorterUsed[optionIndex] === "1") continue;

      const codeword = codewords[optionIndex];
      if (remainder === codeword) return false;

      const updatedShorterUsed = withOption(shorterUsed, optionIndex);
      if (remainder.startsWith(codeword)) {
        addComparison({
          remainder: remainder.slice(codeword.length),
          longerUsed,
          shorterUsed: updatedShorterUsed,
        });
      } else if (codeword.startsWith(remainder)) {
        addComparison({
          remainder: codeword.slice(remainder.length),
          longerUsed: updatedShorterUsed,
          shorterUsed: longerUsed,
        });
      }
    }
  }

  return true;
};
